<template>
  <div class="resource-detail-container">
    <ds-page-header :title="$t('resourceDetail.page.title')" :subtitle="$t('resourceDetail.page.subtitle')">
      <template #extra>
        <span class="ds-num ds-order">{{ resourceDetailInfo.resName }}({{ resourceDetailInfo.resCode }})</span>
      </template>
    </ds-page-header>

    <div class="ds-panel ds-panel--padded">
      <div class="ds-panel__header">
        <span class="ds-panel__title">{{ $t('resourceDetail.itemInfo') }}</span>
        <ds-status-tag size="sm" type="info" :label="resourceDetailInfo.isFixedName || '-'" />
      </div>
      <div class="ds-info-grid">
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('resourceDetail.itemNameWithCode') }}</span>
          <span class="ds-info-grid__value">{{ resourceDetailInfo.resName }}({{ resourceDetailInfo.resCode }})</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('resourceDetail.warehouseName') }}</span>
          <span class="ds-info-grid__value">{{ resourceDetailInfo.shName || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('resourceDetail.itemType') }}</span>
          <span class="ds-info-grid__value">{{ resourceDetailInfo.parentRstName }}{{ resourceDetailInfo.rstName ? ' > ' + resourceDetailInfo.rstName : '' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('resourceDetail.itemSpec') }}</span>
          <span class="ds-info-grid__value">{{ resourceDetailInfo.rssName ? resourceDetailInfo.rssName : '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('resourceDetail.referencePrice') }}</span>
          <span class="ds-info-grid__value ds-num">{{ formatMoney(resourceDetailInfo.price) }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('resourceDetail.chargeStandard') }}</span>
          <span class="ds-info-grid__value ds-num">{{ resourceDetailInfo.outHighPrice == resourceDetailInfo.outLowPrice ? '￥' + resourceDetailInfo.outLowPrice : '￥' + resourceDetailInfo.outLowPrice + '-￥' + resourceDetailInfo.outHighPrice }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('resourceDetail.itemStock') }}</span>
          <span class="ds-info-grid__value ds-num">{{ resourceDetailInfo.stock }}{{ resourceDetailInfo.unitCodeName }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('resourceDetail.minMeasurement') }}</span>
          <span class="ds-info-grid__value ds-num">1{{ resourceDetailInfo.unitCodeName }}={{ resourceDetailInfo.miniUnitStock }}{{ resourceDetailInfo.miniUnitCodeName }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('resourceDetail.minMeasurementTotal') }}</span>
          <span class="ds-info-grid__value ds-num">{{ resourceDetailInfo.miniStock }}{{ resourceDetailInfo.miniUnitCodeName }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('resourceDetail.itemAvgPrice') }}</span>
          <span class="ds-info-grid__value ds-num">{{ resourceDetailInfo.averagePrice ? '￥' + resourceDetailInfo.averagePrice : '￥0' }}</span>
        </div>
      </div>
    </div>

    <div class="ds-panel ds-panel--padded">
      <el-tabs v-model="resourceDetailInfo._currentTab" @tab-click="changeTab(resourceDetailInfo._currentTab)">
        <el-tab-pane :label="$t('resourceDetail.purchaseRecord')" name="resourceDetailPurchase">
          <resource-detail-purchase ref="resourceDetailPurchase"
            v-if="resourceDetailInfo._currentTab === 'resourceDetailPurchase'" />
        </el-tab-pane>
        <el-tab-pane :label="$t('resourceDetail.useRecordText')" name="resourceDetailItemOut">
          <resource-detail-item-out ref="resourceDetailItemOut"
            v-if="resourceDetailInfo._currentTab === 'resourceDetailItemOut'" />
        </el-tab-pane>
        <el-tab-pane :label="$t('resourceDetail.inventoryRecord')" name="resourceDetailInventory">
          <resource-detail-inventory ref="resourceDetailInventory"
            v-if="resourceDetailInfo._currentTab === 'resourceDetailInventory'" />
        </el-tab-pane>
        <el-tab-pane :label="$t('resourceDetail.allocationRecord')" name="resourceDetailAllocation">
          <resource-detail-allocation ref="resourceDetailAllocation"
            v-if="resourceDetailInfo._currentTab === 'resourceDetailAllocation'" />
        </el-tab-pane>
        <el-tab-pane :label="$t('resourceDetail.transferRecord')" name="resourceDetailAllocationUser">
          <resource-detail-allocation-user ref="resourceDetailAllocationUser"
            v-if="resourceDetailInfo._currentTab === 'resourceDetailAllocationUser'" />
        </el-tab-pane>
        <el-tab-pane :label="$t('resourceDetail.usageRecord')" name="resourceDetailUseRecord">
          <resource-detail-use-record ref="resourceDetailUseRecord"
            v-if="resourceDetailInfo._currentTab === 'resourceDetailUseRecord'" />
        </el-tab-pane>
      </el-tabs>
    </div>
  </div>
</template>

<script>
import { getCommunityId } from '@/api/community/communityApi'
import ResourceDetailPurchase from '@/components/resource/resourceDetailPurchase'
import ResourceDetailItemOut from '@/components/resource/resourceDetailItemOut'
import ResourceDetailInventory from '@/components/resource/resourceDetailInventory'
import ResourceDetailAllocation from '@/components/resource/resourceDetailAllocation'
import ResourceDetailAllocationUser from '@/components/resource/resourceDetailAllocationUser'
import ResourceDetailUseRecord from '@/components/resource/resourceDetailUseRecord'
import { getResourceStoreList } from '@/api/resource/resourceDetailApi'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import { formatMoney } from '@/utils/format'

export default {
  name: 'ResourceDetailList',
  components: {
    ResourceDetailPurchase,
    ResourceDetailItemOut,
    ResourceDetailInventory,
    ResourceDetailAllocation,
    ResourceDetailAllocationUser,
    ResourceDetailUseRecord,
    DsPageHeader,
    DsStatusTag
  },
  data() {
    return {
      resourceDetailInfo: {
        resId: '',
        shName: '',
        parentRstName: '',
        rstName: '',
        resName: '',
        resCode: '',
        rssName: '',
        isFixedName: '',
        price: '',
        outHighPrice: '',
        outLowPrice: '',
        stock: '',
        unitCodeName: '',
        miniUnitStock: '',
        miniUnitCodeName: '',
        miniStock: '',
        averagePrice: '',
        _currentTab: 'resourceDetailPurchase',
        needBack: false
      },
      communityId: ''
    }
  },
  created() {
    this.communityId = getCommunityId()
    this.resourceDetailInfo.resId = this.$route.query.resId
    if (!this.resourceDetailInfo.resId) {
      return
    }
    this.loadResourceDetailInfo()
  },
  methods: {
    formatMoney(v) { return formatMoney(v) },
    async loadResourceDetailInfo() {
      try {
        const params = { page: 1, row: 1, resId: this.resourceDetailInfo.resId, communityId: this.communityId }
        const { resourceStores } = await getResourceStoreList(params)
        if (resourceStores && resourceStores.length > 0) {
          Object.assign(this.resourceDetailInfo, resourceStores[0])
          this.changeTab(this.resourceDetailInfo._currentTab)
        }
      } catch (error) {
        console.error('Failed to load resource detail:', error)
      }
    },
    changeTab(tabName) {
      this.resourceDetailInfo._currentTab = tabName
      setTimeout(() => {
        const tabRef = this.$refs[`${tabName}`]
        if (tabRef && tabRef.loadData) {
          tabRef.loadData({ resId: this.resourceDetailInfo.resId })
        }
      }, 500)
    }
  }
}
</script>

<style lang="scss" scoped>
.resource-detail-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;
  margin-bottom: var(--ds-spacing-lg);
  &--padded { padding: var(--ds-spacing-lg); }
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: var(--ds-spacing-md);
    margin-bottom: var(--ds-spacing-lg);
    border-bottom: 1px solid var(--ds-gray-200);
  }
  &__title { font-size: var(--ds-font-size-lg); font-weight: 600; color: var(--ds-gray-800); }
}
.ds-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: var(--ds-spacing-md) var(--ds-spacing-lg);
  &__item { display: flex; flex-direction: column; gap: 4px; }
  &__label { font-size: var(--ds-font-size-sm); color: var(--ds-gray-500); }
  &__value { font-size: var(--ds-font-size-md); color: var(--ds-gray-800); word-break: break-all; }
}
.ds-order { font-size: var(--ds-font-size-sm); color: var(--ds-gray-600); }
</style>
